import { Avatar } from '@boringer-avatars/react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import React, { useMemo } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import DropdownMenuMultiselect from './Multiselect';
import {
  MarkingOptionContainer,
  StyledMenuItem,
  StyledHeading,
  StyledBox,
  FlexContainer,
  FlexContainerSmall,
  StyledListItemText,
  StyledListItemTextBold,
  Frame12841,
  StyledTextField,
} from './style';

export const DropdownMenu = (props) => {
  if (props.multiselect) {
    const { multiselect: _m, ...multiselectProps } = props;
    return <DropdownMenuMultiselect {...multiselectProps} />;
  }

  const {
    selectedIndex,
    markingCriteriaType,
    menuItems = [],
    onItemSelected,
    withCheckbox,
    showAvatar = false,
    small,
    fullWidth,
    primaryText,
    noDefaultSelected = false,
    defaultSearch = false,
    getSelectedItem = null,
    setIsChanged,
    borderColor,
    borderRadius,
    compact = false,
    leadingIcon,
    borderlessMenuItems = false,
  } = props;

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [searchInput, setSearchInput] = React.useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 250);
  const filteredMenuItems = useMemo(() => {
    if (!debouncedSearch) return menuItems;
    const term = debouncedSearch.toLowerCase();
    return menuItems.filter((item) => {
      const text = item.title ?? item.heading ?? '';
      return String(text).toLowerCase().includes(term);
    });
  }, [menuItems, debouncedSearch]);

  const handleSearchInputChange = (event) => setSearchInput(event.target.value);

  const initialSelectedItem =
    selectedIndex === undefined || selectedIndex < 0
      ? findFirstSelectableItem(menuItems, noDefaultSelected)
      : menuItems[selectedIndex];
  const [selectedItem, setSelectedItem] = React.useState(initialSelectedItem);

  const menuRef = React.useRef(null);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (item) => {
    if (getSelectedItem !== null) {
      getSelectedItem(item);
    }
    if (item) {
      setSelectedItem(item);
      setAnchorEl(null);
      if (item.link) {
        window.location.href = item.link;
        window.location.reload();
        return;
      }
      if (item.onClick) {
        item.onClick(item);
        return;
      }
      if (onItemSelected) {
        if (setIsChanged) setIsChanged(true);
        onItemSelected(item);
      }
    } else {
      setSelectedItem(menuItems[0]);
      setAnchorEl(null);
    }
  };

  return (
    <div style={fullWidth ? { width: '100%' } : {}}>
      <StyledBox
        style={
          fullWidth
            ? {
                borderColor: borderColor || '#959595',
                borderRadius: borderRadius || '12px',
                padding: compact ? '0 8px !important' : '0px 20px !important',
                ...(borderColor ? { boxShadow: 'none' } : {}),
              }
            : {
                borderColor: borderColor || 'var(--light-mode-purple)',
                borderRadius: borderRadius || '12px',
                padding: selectedItem?.type === 'FOLDER' ? '7px' : compact ? '0 8px' : '0',
              }
        }
      >
        {small ? (
          <FlexContainerSmall onClick={handleClick}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleClick}
            >
              {createImageFrame(selectedItem, showAvatar)}
            </IconButton>
            <div className="text-container" onClick={handleClick}>
              <p>
                <StyledListItemText
                  id={selectedItem?.id}
                  primary={selectedItem?.title || selectedItem?.name}
                />
              </p>
            </div>
            <IconButton onClick={handleClick}>
              <Frame12841 src="/img/frame-1284@2x.png" alt="Open dropdown" />
            </IconButton>
          </FlexContainerSmall>
        ) : (
          <FlexContainer
            onClick={handleClick}
            style={markingCriteriaType ? { width: '100px' } : {}}
          >
            {showAvatar && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleClick}
              >
                {createImageFrame(selectedItem, showAvatar)}
              </IconButton>
            )}
            <div
              className="text-container"
              onClick={handleClick}
              style={{
                padding: compact ? '3px 6px' : '5px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
              }}
            >
              {leadingIcon ? (
                <img
                  src={leadingIcon}
                  alt=""
                  style={{
                    width: '16px',
                    height: '16px',
                    flexShrink: 0,
                  }}
                />
              ) : null}
              <StyledListItemText
                id={selectedItem?.id}
                primary={
                  primaryText
                    ? primaryText
                    : selectedItem?.title || selectedItem?.name
                }
              />
            </div>
            <IconButton onClick={handleClick} size={compact ? 'small' : 'medium'}>
              <Frame12841 src="/img/frame-1284@2x.png" alt="Open dropdown" />
            </IconButton>
          </FlexContainer>
        )}
      </StyledBox>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        getContentAnchorEl={null}
        ref={menuRef}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: borderRadius || (markingCriteriaType ? '12px' : '8px'),
            width: fullWidth && anchorEl ? `${anchorEl.offsetWidth}px` : 'fit-content',
            overflow: 'hidden',
            minWidth: fullWidth && anchorEl ? `${anchorEl.offsetWidth}px` : undefined,
          },
          '& .MuiMenu-paper': {
            '& .MuiList-root': {
              paddingTop: 0, // remove top padding
              paddingBottom: 0, // remove bottom padding
            },
          },
        }}
      >
        {defaultSearch && (
          <StyledTextField
            variant="outlined"
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyDown={(e) => e.stopPropagation()}
            fullWidth
          />
        )}
        {filteredMenuItems?.map((item) => {
          if (item.heading) {
            return withHeadings(
              item,
              handleClose,
              markingCriteriaType,
              withCheckbox,
              showAvatar,
              borderlessMenuItems,
            );
          }
          return markingCriteriaType ? (
            <StyledMenuItem
              key={item.id}
              $borderless={borderlessMenuItems}
              onClick={() => handleClose(item)}
            >
              <MarkingOptionContainer>
                <StyledListItemTextBold
                  primary={item.name}
                  secondary={item.description}
                />
              </MarkingOptionContainer>
            </StyledMenuItem>
          ) : (
            <StyledMenuItem
              key={item.id}
              $borderless={borderlessMenuItems}
              onClick={() => handleClose(item)}
            >
              {withCheckbox && <CustomCheckbox />}
              {createImageFrame(item, showAvatar)}
              <div className="text-container">
                <p>
                  <StyledListItemText primary={item.title || item.name} />
                </p>
              </div>
            </StyledMenuItem>
          );
        })}
      </Menu>
    </div>
  );
};
function withHeadings(
  entry,
  handleClose,
  markingCriteriaType,
  withCheckbox,
  showAvatar,
  borderlessMenuItems,
) {
  const headingItems = Array.isArray(entry?.items) ? entry.items : [];
  return (
    <div key={entry?.heading ?? 'heading'}>
      {/* Render the heading */}
      <StyledMenuItem disabled $borderless={borderlessMenuItems}>
        <StyledHeading>{entry?.heading}</StyledHeading>
      </StyledMenuItem>

      {/* Render the items under the heading */}
      {headingItems.map((item) => (
        <StyledMenuItem
          key={item.id}
          $borderless={borderlessMenuItems}
          onClick={() => handleClose(item)}
        >
          {markingCriteriaType ? (
            <MarkingOptionContainer>
              <StyledListItemTextBold
                primary={item.name}
                secondary={item.description}
              />
            </MarkingOptionContainer>
          ) : (
            <>
              {withCheckbox && <CustomCheckbox />}
              {createImageFrame(item, showAvatar)}
              <div className="text-container">
                <p>
                  <StyledListItemText primary={item.title || item.name} />
                </p>
              </div>
            </>
          )}
        </StyledMenuItem>
      ))}
    </div>
  );
}

function createImageFrame(selectedItem, showAvatar) {
  if (selectedItem?.image) {
    return <Frame12841 src={selectedItem.image} alt="Frame 1284" />;
  }
  if (!showAvatar) return <></>;
  return (
    <Avatar
      title={false}
      size={25}
      variant="beam"
      name={selectedItem?.title}
      square={false}
    />
  );
}
const findFirstSelectableItem = (menuItems, noDefaultSelected) => {
  if (noDefaultSelected) {
    return null;
  }
  for (let entry of menuItems) {
    if (entry.heading) {
      if (entry.items && entry.items.length > 0) {
        return entry.items[0];
      }
    } else {
      return entry;
    }
  }
  return null;
};

export default DropdownMenu;
